<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ContactInfoRow from './ContactInfoRow.vue';
import EmojiOrIcon from 'shared/components/EmojiOrIcon.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import KisoftContactEdit from 'dashboard/components-next/sidebar/KisoftContactEdit.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import NextButton from 'dashboard/components-next/button/Button.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';

import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';
import { emitter } from 'shared/helpers/mitt';

export default {
  components: {
    NextButton,
    ContactInfoRow,
    EmojiOrIcon,
    EditContact,
    KisoftContactEdit,
    Avatar,
    ComposeConversation,
    SocialIcons,
    ContactMergeModal,
    VoiceCallButton,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['panelClose'],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      showEditModal: false,
      showDeleteModal: false,
      editingName: false,
      nameDraft: '',
      editingPhone: false,
      phoneDraft: '',
      savingField: false,
      editingCaptacao: false,
      addingNewCaptacao: false,
      newCaptacaoText: '',
      macroHover: false,
      macroExecutando: false,
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'contacts/getUIFlags' }),
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    captacaoDef() {
      return (this.$store.getters['attributes/getAttributes'] || []).find(
        a => a.attribute_key === 'forma_captacao'
      );
    },
    captacaoOptions() {
      const def = this.captacaoDef;
      const vals = (def && def.attribute_values) || [];
      return vals.length ? vals : ['Instagram', 'Indicação', 'Youtube', 'Tiktok', 'Google'];
    },
    formaCaptacao() {
      return (this.contact.custom_attributes || {}).forma_captacao || '';
    },
    captacaoRowStyle() {
      if (this.editingCaptacao) return {};
      if (!this.formaCaptacao) return { color: '#dc2626', fontWeight: '600' };
      return { color: this.captacaoBg(this.formaCaptacao), fontWeight: '600' };
    },
    macroCadastro() {
      const list = this.$store.getters['macros/getMacros'] || [];
      return list.find(
        m => String(m.name || '').trim().toLowerCase() === 'pedido cadastro dashboard'
      ) || null;
    },
    macroCadastroTexto() {
      const m = this.macroCadastro;
      if (!m) return '';
      const send = (m.actions || []).find(a => a.action_name === 'send_message');
      const p = send && send.action_params;
      return Array.isArray(p) ? String(p[0] || '') : String(p || '');
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;
      return {
        twitter: twitterScreenName,
        telegram: telegramUsername,
        ...(socialProfiles || {}),
      };
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
  },
  mounted() {
    const recs = this.$store.getters['attributes/getAttributes'];
    if (!recs || !recs.length) {
      this.$store.dispatch('attributes/get');
    }
    const ms = this.$store.getters['macros/getMacros'];
    if (!ms || !ms.length) {
      this.$store.dispatch('macros/get');
    }
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
  },
  methods: {
    dynamicTime,
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    openComposeConversationModal(toggleFn) {
      toggleFn();
      // Flag to prevent triggering drag n drop,
      // When compose modal is active
      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
    },
    closeComposeConversationModal() {
      // Flag to enable drag n drop,
      // When compose modal is closed
      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showEditModal = false;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({
            name: 'inbox_view',
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    async executarMacroCadastro() {
      if (this.macroExecutando) return;
      const m = this.macroCadastro;
      const chat = this.$store.getters.getSelectedChat;
      const convId = chat && chat.id;
      if (!m || !convId) {
        useAlert('Macro ou conversa nao encontrada');
        return;
      }
      try {
        this.macroExecutando = true;
        await this.$store.dispatch('macros/execute', {
          macroId: m.id,
          conversationIds: [convId],
        });
        useAlert('Macro enviada');
      } catch (error) {
        useAlert('Falha ao executar a macro');
      } finally {
        this.macroExecutando = false;
        this.macroHover = false;
      }
    },
    captacaoBg(opt) {
      const m = { 'Instagram': '#E1306C', 'Indicação': '#16a34a', 'Indicacao': '#16a34a', 'Youtube': '#FF0000', 'Tiktok': '#111827', 'Google': '#4285F4' };
      return m[opt] || '#64748b';
    },
    startEditCaptacao() {
      this.addingNewCaptacao = false;
      this.newCaptacaoText = '';
      this.editingCaptacao = true;
    },
    onCaptacaoChange(ev) {
      const v = ev.target.value;
      if (v === '__novo__') {
        this.addingNewCaptacao = true;
        this.$nextTick(() => this.$refs.captacaoNovo && this.$refs.captacaoNovo.focus());
        return;
      }
      this.saveCaptacao(v);
    },
    async saveNewCaptacao() {
      const val = (this.newCaptacaoText || '').trim();
      if (!val) { this.editingCaptacao = false; this.addingNewCaptacao = false; return; }
      const def = this.captacaoDef;
      if (def && def.id && !(def.attribute_values || []).includes(val)) {
        try {
          await this.$store.dispatch('attributes/update', {
            id: def.id,
            attribute_values: [...(def.attribute_values || []), val],
          });
        } catch (e) { /* ignora falha ao persistir a opcao nova */ }
      }
      await this.saveCaptacao(val);
    },
    async saveCaptacao(val) {
      this.editingCaptacao = false;
      this.addingNewCaptacao = false;
      if (!val || val === this.formaCaptacao) return;
      try {
        this.savingField = true;
        await this.$store.dispatch('contacts/update', {
          id: this.contact.id,
          custom_attributes: { ...(this.contact.custom_attributes || {}), forma_captacao: val },
        });
        useAlert('Forma de captação atualizada');
      } catch (error) {
        useAlert((error && error.message) || 'Falha ao salvar');
      } finally {
        this.savingField = false;
      }
    },
    startEditName() {
      this.nameDraft = this.contact.name || '';
      this.editingName = true;
      this.$nextTick(() => this.$refs.nameInput && this.$refs.nameInput.focus());
    },
    async saveName() {
      if (!this.editingName) return;
      this.editingName = false;
      const val = (this.nameDraft || '').trim();
      if (!val || val === this.contact.name) return;
      await this.saveContactField({ name: val });
    },
    startEditPhone() {
      this.phoneDraft = this.contact.phone_number || '';
      this.editingPhone = true;
      this.$nextTick(() => this.$refs.phoneInput && this.$refs.phoneInput.focus());
    },
    async savePhone() {
      if (!this.editingPhone) return;
      this.editingPhone = false;
      const val = this.normalizarTelefone(this.phoneDraft);
      if (val === (this.contact.phone_number || '')) return;
      await this.saveContactField({ phone_number: val });
    },
    normalizarTelefone(raw) {
      const s = (raw || '').trim();
      if (!s) return '';
      const temMais = s.charAt(0) === '+';
      let d = '';
      for (let i = 0; i < s.length; i++) {
        const ch = s.charAt(i);
        if (ch >= '0' && ch <= '9') d += ch;
      }
      if (!d) return '';
      if (temMais) return '+' + d;
      if ((d.length === 12 || d.length === 13) && d.indexOf('55') === 0) return '+' + d;
      return '+55' + d;
    },
    async saveContactField(fields) {
      try {
        this.savingField = true;
        await this.$store.dispatch('contacts/update', { id: this.contact.id, ...fields });
        useAlert('Contato atualizado');
      } catch (error) {
        if (fields.phone_number) {
          await this.avisarTelefoneDuplicado(fields.phone_number, error);
        } else {
          useAlert((error && error.message) || 'Falha ao atualizar contato');
        }
      } finally {
        this.savingField = false;
      }
    },
    async avisarTelefoneDuplicado(phone, error) {
      try {
        const acc = this.$route.params.accountId;
        const resp = await window.axios.get('/api/v1/accounts/' + acc + '/contacts/search', { params: { q: phone } });
        const lista = (resp.data && resp.data.payload) || [];
        const outro = lista.find(c => c.phone_number === phone && c.id !== this.contact.id);
        if (outro) {
          const nome = outro.name || ('#' + outro.id);
          if (window.confirm('Ja existe o contato ' + nome + ' com este telefone. Abrir esse contato?')) {
            this.$router.push('/app/accounts/' + acc + '/contacts/' + outro.id);
          }
          return;
        }
      } catch (e) { /* cai no alerta generico */ }
      useAlert((error && error.message) || 'Ja existe outro contato com este telefone.');
    },
    openMergeModal() {
      this.$refs.mergeModal?.open();
    },
  },
};
</script>

<template>
  <div class="relative items-center w-full p-4">
    <div class="flex flex-col w-full gap-2 text-left rtl:text-right">

      <div class="flex flex-col items-start gap-1.5 min-w-0 w-full">
        <div v-if="showAvatar" class="flex items-center w-full min-w-0 gap-3">
          <h3
            v-if="!editingName"
            class="flex-shrink max-w-full min-w-0 my-0 text-base capitalize break-words text-n-slate-12"
          >
            {{ contact.name }}
          </h3>
          <input
            v-else
            ref="nameInput"
            v-model="nameDraft"
            type="text"
            class="flex-shrink max-w-full min-w-0 px-1.5 py-0.5 text-base border border-gray-300 dark:border-gray-600 rounded bg-transparent text-n-slate-12"
            @keyup.enter="saveName"
            @keyup.esc="editingName = false"
            @blur="saveName"
          />
          <div class="flex flex-row items-center gap-2">
            <span
              v-tooltip.top="'Editar nome'"
              class="i-ph-pencil-simple text-sm cursor-pointer text-n-slate-10 hover:text-n-slate-12"
              @click="startEditName"
            />
          </div>
        </div>

        <p v-if="additionalAttributes.description" class="break-words mb-0.5">
          {{ additionalAttributes.description }}
        </p>
        <div class="flex flex-col items-start w-full gap-2">
          <div v-if="!editingPhone" :class="contact.phone_number ? 'bg-green-100 dark:bg-green-500/20' : 'bg-red-100 dark:bg-red-500/25'" class="flex items-center gap-1.5 min-h-[1.5rem] rounded px-2 py-1 text-n-slate-11">
            <EmojiOrIcon
              icon="call"
              emoji="📞"
              icon-size="14"
              class="flex-shrink-0"
            />
            <a
              v-if="contact.phone_number"
              :href="`tel:${contact.phone_number}`"
              class="text-sm whitespace-nowrap hover:underline"
            >{{ contact.phone_number }}</a>
            <span v-else class="text-sm text-n-slate-11">{{ $t('CONTACT_PANEL.NOT_AVAILABLE') }}</span>
            <span
              v-tooltip.top="'Editar telefone'"
              class="i-ph-pencil-simple text-sm shrink-0 cursor-pointer text-n-slate-10 hover:text-n-slate-12"
              @click="startEditPhone"
            />
          </div>
          <div v-else class="flex items-center w-full gap-1">
            <input
              ref="phoneInput"
              v-model="phoneDraft"
              type="text"
              placeholder="+55..."
              class="w-full px-1.5 py-0.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-transparent text-n-slate-12"
              @keyup.enter="savePhone"
              @keyup.esc="editingPhone = false"
              @blur="savePhone"
            />
          </div>
          <ContactInfoRow
            v-if="contact.identifier"
            :value="contact.identifier"
            icon="contact-identify"
            emoji="🪪"
            :title="$t('CONTACT_PANEL.IDENTIFIER')"
          />
          <div :style="captacaoRowStyle" class="flex items-center gap-1.5 min-h-[1.5rem] text-n-slate-11">
            <span class="text-sm flex-shrink-0" title="Forma de captação">📡</span>
            <template v-if="!editingCaptacao">
              <span class="text-sm whitespace-nowrap">{{ formaCaptacao || '-' }}</span>
              <span
                v-tooltip.top="'Editar forma de captação'"
                class="i-ph-pencil-simple text-sm shrink-0 cursor-pointer text-n-slate-10 hover:text-n-slate-12"
                @click="startEditCaptacao"
              />
            </template>
            <select
              v-else-if="!addingNewCaptacao"
              class="flex-1 min-w-0 max-w-full px-1 py-0.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-transparent text-n-slate-12"
              @change="onCaptacaoChange"
            >
              <option value="" disabled :selected="!formaCaptacao">Selecione...</option>
              <option
                v-for="opt in captacaoOptions"
                :key="opt"
                :value="opt"
                :selected="opt === formaCaptacao"
                :style="{ color: captacaoBg(opt) }"
              >{{ opt }}</option>
              <option value="__novo__">+ Digitar novo...</option>
            </select>
            <input
              v-else
              ref="captacaoNovo"
              v-model="newCaptacaoText"
              type="text"
              placeholder="Nova forma..."
              class="flex-1 min-w-0 max-w-full px-1.5 py-0.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-transparent text-n-slate-12"
              @keyup.enter="saveNewCaptacao"
              @keyup.esc="editingCaptacao = false"
              @blur="saveNewCaptacao"
            />
          </div>
          <ContactInfoRow
            v-if="location || additionalAttributes.location"
            :value="location || additionalAttributes.location"
            icon="map"
            emoji="🌍"
            :title="$t('CONTACT_PANEL.LOCATION')"
          />
          <SocialIcons :social-profiles="socialProfiles" />
        </div>
      </div>
      <div class="flex items-center w-full mt-0.5 gap-2">
        <ComposeConversation
          :contact-id="String(contact.id)"
          is-modal
          @close="closeComposeConversationModal"
        >
          <template #trigger="{ toggle }">
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
              icon="i-ph-chat-circle-dots"
              slate
              faded
              sm
              @click="openComposeConversationModal(toggle)"
            />
          </template>
        </ComposeConversation>
        <VoiceCallButton
          :phone="contact.phone_number"
          :contact-id="contact.id"
          icon="i-ri-phone-fill"
          size="sm"
          :tooltip-label="$t('CONTACT_PANEL.CALL')"
          slate
          faded
        />
        <NextButton
          v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
          icon="i-ph-pencil-simple"
          slate
          faded
          sm
          @click="toggleEditModal"
        />
        <span
          v-if="macroCadastro"
          class="relative inline-flex"
          @mouseenter="macroHover = true"
          @mouseleave="macroHover = false"
        >
          <NextButton
            v-tooltip.top-end="'Pedido cadastro dashboard'"
            icon="i-lucide-play"
            slate
            faded
            sm
            @click="executarMacroCadastro"
          />
          <div
            v-if="macroHover && macroCadastroTexto"
            class="absolute z-50 bottom-9 left-0 w-60 p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1e1e1e] shadow-lg text-xs whitespace-pre-line text-n-slate-12"
          >{{ macroCadastroTexto }}</div>
        </span>
      </div>
      <KisoftContactEdit
        v-if="showEditModal"
        :contact-id="contact.id"
        @close="toggleEditModal"
      />
      <ContactMergeModal ref="mergeModal" :primary-contact="contact" />
    </div>
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
  </div>
</template>
